import { z } from 'zod';
import type { BrandKit } from '../brand';
import type { LocaleId } from '../locales';
import { applyAutoSfx } from '../motion/auto-sfx';
import type { TemplateDoc } from '../template';
import { VideoBuilder, type SceneSpec, type TransitionSpec } from '../video-gallery/kit';
import { BLOCK_IDS, BLOCK_MAP, BLOCKS, findVariant, type BlockId, type Theme } from './blocks';
import { videoContentSchema, type VideoContent } from './content';
import { GEN_PALETTES, paletteFromBrand, type GenPalette } from './palette';

/**
 * Generated videos: new templates composed from the user's own content (texts, photos, logo,
 * brand kit) instead of picked from the library. A storyboard says which scenes, in which
 * layouts, colours, transitions and pace; the blocks build them. Storyboards come from the
 * planner (free, seeded composition) and later from a model writing the same small JSON.
 */

export * from './content';
export { BLOCKS, BLOCK_IDS, type BlockId, type Theme } from './blocks';
export { GEN_PALETTES, paletteFromBrand } from './palette';
export type { GenPalette } from './palette';

/** Transitions drawn over the cut or moving whole scenes: safe before any scene. */
const OPEN_TRANSITIONS = [
  'slide-over',
  'push',
  'zoom-in',
  'zoom-out',
  'crossfade',
  'whip-pan',
  'blur',
  'stack',
  'glitch',
  'panels',
  'diagonal',
  'shutter',
  'slices',
  'stripes',
  'doors',
  'circle',
  'flash',
] as const;
/** Clip transitions: only into scenes whose layers do not animate clips themselves. */
const CLIP_TRANSITIONS = ['wipe', 'iris'] as const;
const DIRECTIONAL = new Set(['slide-over', 'push', 'whip-pan', 'wipe']);
const DIRECTIONS = ['left', 'right', 'up', 'down'] as const;

export const storyboardSchema = z.object({
  seed: z.number().int(),
  /** A palette id from GEN_PALETTES, or "brand" (derived from the brand kit). */
  palette: z.string().max(32),
  /** 0.8 (calm) … 1.25 (fast): scales the scene lengths. */
  pace: z.number().min(0.6).max(1.6).default(1),
  scenes: z
    .array(
      z.object({
        block: z.enum(BLOCK_IDS),
        variant: z.string().max(32),
        theme: z.enum(['dark', 'light']),
        transition: z
          .object({
            preset: z.string().max(32),
            duration: z.number().min(100).max(1500).optional(),
            params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
          })
          .nullable()
          .optional(),
      }),
    )
    .min(2)
    .max(8),
});
export type Storyboard = z.infer<typeof storyboardSchema>;

/** Seeded random numbers (mulberry32). */
export function genRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let x = a;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

const pickOne = <T>(r: () => number, items: readonly T[]): T =>
  items[Math.floor(r() * items.length)];
function shuffle<T>(r: () => number, items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Variants of a block the content can fill. */
function fitting(block: BlockId, c: VideoContent, theme?: Theme) {
  return (BLOCK_MAP.get(block)?.variants ?? []).filter(
    (v) => v.fits(c) && (!theme || !v.themes || v.themes.includes(theme)),
  );
}

/**
 * A free storyboard for the content: a hook, two to four middle scenes the content can fill, in
 * any order, and a call to action — each in a random layout, with random themes, transitions
 * and pace. The same seed gives the same storyboard.
 */
export function planVideo(
  content: VideoContent,
  opts: { seed: number; brand?: boolean },
): Storyboard {
  const r = genRng(opts.seed);
  const c = videoContentSchema.parse(content);
  const middle = BLOCKS.filter((b) => b.place === 'middle' && fitting(b.id, c).length);
  const count = Math.min(middle.length, 2 + Math.floor(r() * 3));
  const chosen = shuffle(r, middle).slice(0, count);
  const blocks: BlockId[] = ['hook', ...chosen.map((b) => b.id), 'cta'];

  // Themes: mostly alternating, sometimes two in a row
  let theme: Theme = r() < 0.6 ? 'dark' : 'light';
  const scenes: Storyboard['scenes'] = [];
  blocks.forEach((block, i) => {
    if (i > 0 && r() < 0.75) theme = theme === 'dark' ? 'light' : 'dark';
    let variants = fitting(block, c, theme);
    if (!variants.length) {
      theme = theme === 'dark' ? 'light' : 'dark';
      variants = fitting(block, c, theme);
    }
    const v = pickOne(r, variants);
    scenes.push({ block, variant: v.id, theme });
  });
  // Transitions: into each next scene, clip ones only where that scene has no clips of its own
  scenes.forEach((s, i) => {
    const next = scenes[i + 1];
    if (!next) return;
    const clipNext = findVariant(next.block, next.variant)?.clip;
    const pool: readonly string[] =
      clipNext || r() < 0.75 ? OPEN_TRANSITIONS : [...OPEN_TRANSITIONS, ...CLIP_TRANSITIONS];
    const preset = pickOne(r, pool);
    const t: TransitionSpec = { preset, duration: 450 + Math.round(r() * 3) * 100 };
    if (DIRECTIONAL.has(preset)) t.params = { direction: pickOne(r, DIRECTIONS) };
    s.transition = t;
  });
  return {
    seed: opts.seed,
    palette: opts.brand ? 'brand' : pickOne(r, GEN_PALETTES).id,
    pace: Math.round((0.85 + r() * 0.35) * 100) / 100,
    scenes,
  };
}

/** Several different storyboards for one content (no two with the same scenes and layouts). */
export function planVideos(
  content: VideoContent,
  opts: { seed: number; count: number; brand?: boolean },
): Storyboard[] {
  const out: Storyboard[] = [];
  const seen = new Set<string>();
  for (let i = 0; out.length < opts.count && i < opts.count * 8; i++) {
    const sb = planVideo(content, { seed: (opts.seed + i * 7919) >>> 0, brand: opts.brand });
    const key = sb.scenes.map((s) => `${s.block}:${s.variant}`).join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(sb);
  }
  return out;
}

/** Shortest generated video, ms (scene lengths before transitions overlap them). */
const MIN_VIDEO_MS = 6500;

export interface GenBrand {
  id?: string | null;
  kit?: BrandKit | null;
}

/** The palette of a storyboard: the brand's own, else the named one. */
export function storyboardPalette(sb: Storyboard, brand?: GenBrand | null): GenPalette {
  const named = GEN_PALETTES.find((p) => p.id === sb.palette) ?? GEN_PALETTES[0];
  if (brand?.kit && sb.palette === 'brand') return paletteFromBrand(brand.kit, named);
  return named;
}

/** Build the video of a storyboard as a template document. */
export function buildGeneratedVideo(
  storyboard: Storyboard,
  content: VideoContent,
  opts: { width: number; height: number; locale: LocaleId; brand?: GenBrand | null },
): TemplateDoc {
  const sb = storyboardSchema.parse(storyboard);
  const c = videoContentSchema.parse(content);
  const brandLogo = opts.brand?.kit?.logos?.logo;
  // The brand's logo when the content has none, as a reference that follows the brand
  if (!c.logo && brandLogo) c.logo = `brand:logo|${brandLogo}`;
  const palette = storyboardPalette(sb, opts.brand);
  const used = sb.scenes.map((s) => {
    const v = findVariant(s.block, s.variant);
    if (!v || !v.fits(c)) throw new Error(`Scene ${s.block}/${s.variant} does not fit the content`);
    return { s, v };
  });
  // Short storyboards (a hook and a call to action) are stretched to a watchable length
  const raw = used.map(({ v }) => v.duration(c) / sb.pace);
  const stretch = Math.max(1, MIN_VIDEO_MS / raw.reduce((a, d) => a + d, 0));
  const specs: SceneSpec[] = used.map(({ s }, i) => ({
    key: `${s.block}${i + 1}`,
    name: BLOCK_MAP.get(s.block)!.name,
    duration: Math.round((raw[i] * stretch) / 50) * 50,
    transition: s.transition ?? null,
  }));
  const b = new VideoBuilder(opts, specs);
  const r = genRng(sb.seed ^ 0x9e3779b9);
  used.forEach(({ s, v }, i) => {
    const sc = b.sc(specs[i].key);
    v.build({ b, sc, c, p: palette, theme: s.theme, rnd: r });
    // Camera accents that end where they started: camera moves add up over the whole video,
    // so a push-in per scene would keep zooming in scene after scene
    const cam = r();
    if (cam < 0.3 && i > 0) b.addCamera(sc, 'zoom-burst', 0, 700, { intensity: 0.5 });
    else if (cam < 0.55)
      b.addCamera(sc, 'punch', 700 + Math.round(r() * 6) * 100, 450, { intensity: 0.8 });
  });
  b.poster = specs[0].key;
  const doc = applyAutoSfx(b.build(null));
  if (opts.brand?.id) doc.brandId = opts.brand.id;
  return doc;
}

/** A short description of a storyboard for the cards: "Hook · Product · Offer · Call to action". */
export function storyboardSummary(sb: Storyboard, locale: LocaleId): string {
  return sb.scenes.map((s) => BLOCK_MAP.get(s.block)?.name[locale] ?? s.block).join(' · ');
}
